import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classNames from "../../../../utils/classNames";
import useFocusTrap from "../../../../hooks/useFocusTrap";
import IconButton from "../../../atoms/IconButton";
import { CloseIcon } from "../../../atoms/Iconography/NavigationControls.jsx";
import { TabMenu } from "../../../molecules/TabMenu";
import { Logo } from "../../../molecules/Logo";
import { UserSettings } from "../UserSettings";

import "./MobileNavigation.scss";

/* Overlays opened from inside the drawer render here rather than in the global
   `#portal`: the drawer is an `aria-modal` dialog, so anything outside its
   subtree can be treated as unavailable by assistive tech. The host sits beside
   the sliding panel — never inside it, because the panel's transform would
   become the containing block for the fixed-positioned overlays. */
export const DRAWER_OVERLAY_PORTAL_ID = "mobile-nav-drawer-overlays";

export function MobileNavDrawer({ isOpen, tabs, activeTab, onSelect, onClose }) {
    const { t } = useTranslation();
    const drawerRef = useRef(null);

    useFocusTrap({
        active: isOpen,
        containerRef: drawerRef,
    });

    useEffect(() => {
        if (typeof document === "undefined") {
            return undefined;
        }

        const originalOverflow = document.body.style.overflow;
        if (isOpen) {
            document.body.style.overflow = "hidden";
        }

        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    return (
        <div
            ref={drawerRef}
            className="mobile-nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={t("navigation.drawerAria")}
        >
            <div className="mobile-nav-drawer__backdrop" onClick={onClose} />
            <div className={classNames("mobile-nav-drawer__panel", { "mobile-nav-drawer__panel--open": isOpen })}>
                <div className="mobile-nav-drawer__header">
                    <h2 className="mobile-nav-drawer__title">
                        <Logo variant="inline" />
                    </h2>
                    <IconButton
                        icon={<CloseIcon />}
                        label={t("navigation.close")}
                        onClick={onClose}
                        variant="ghost"
                        size={40}
                    />
                </div>
                <div className="mobile-nav-drawer__content">
                    <TabMenu
                        tabs={tabs}
                        activeTab={activeTab}
                        onClick={(tabId) => {
                            onSelect(tabId);
                            onClose();
                        }}
                        orientation="vertical"
                    />
                </div>
                <div className="navigation-panel__footer">
                    <UserSettings portalRootId={DRAWER_OVERLAY_PORTAL_ID} />
                </div>
            </div>
            <div id={DRAWER_OVERLAY_PORTAL_ID} className="mobile-nav-drawer__overlays" />
        </div>
    );
}

MobileNavDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    tabs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.node.isRequired,
    })).isRequired,
    activeTab: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};
